/**
 * @module app.cm
 */

import { autoinject, observable, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IPopup } from 'app/shared/elements/popup-base';
import { RouteConfig, NavigationInstruction } from 'aurelia-router';
import { Toaster } from 'app/shared/lib/toaster';
import { CaseService } from 'app/cm/services/case-service';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ExtendLocalState, applyLocalState } from 'app/shared/models/local-state-extensions';
import { CaseSelectCustomEvent, OpenCaseQueuesCustomEvent } from 'app/cm/components/case/case-owner-cases-grid';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { safe } from 'app/shared/lib/utilities';
import { AssignCasesPageContext } from 'app/cm/lib/assign-cases-context';
import { CaseQueueHistoryGrid } from 'app/cm/components/common/case-queue-history-grid';
import { CaseWithLocalState } from 'app/cm/models/local-state-extensions';
import * as CM from 'app/cm/models/essette-api';
import { allActionsSucceeded } from 'app/shared/lib/essette-api';
import { guid } from 'app/shared/lib/guid';
import { I18N } from 'aurelia-i18n';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AssignCasesClearBulkActionEvent } from 'app/cm/lib/events';

interface ICaseOwnerLocalState { isLoaded?: boolean; }
type CaseOwnerWithLocalState = ExtendLocalState<CM.CaseOwner, ICaseOwnerLocalState>;
type Choice = 'assign' | 'decline' | '';

const expandedOwnersCache = new Map<AssignCasesPage, ReadonlyArray<CM.CaseOwner>>();

@autoinject()
export class AssignCasesPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected owners: ReadonlyArray<CaseOwnerWithLocalState> = [];
    protected statuses: ReadonlyArray<CM.CaseStatus> = [];
    protected ownersWithCases: ReadonlyArray<CaseOwnerWithLocalState> = [];

    protected choice: Choice = '';
    protected selectedUser: CM.CaseOwner | null = null;
    @observable
    protected selectedStatus: CM.CaseStatusWithReasonModel | null = null;
    protected selectedStatusReason: CM.CaseStatusReason | null = null;
    protected selectedCount: number = 0;
    protected caseQueueHistory: CM.Case | null = null;

    protected selectAtLeastOne: string = '';
    protected selectedSingular: string = '';
    protected selectedPlural: string = '';

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10000;
    protected casePageSize: number = 20;

    protected hasCaseAssign: boolean = false;

    protected invalidCount: number = 0;
    private verifyState: number = 0;
    private verifiedCases: CaseWithLocalState[] = [];
    protected id: guid = guid();


    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<CM.CaseOwner>;
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $queueHistory!: CaseQueueHistoryGrid;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: AssignCasesPageContext,
        private readonly service: CaseService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.selectAtLeastOne = this.i18n.tr('ui:messages.info.selectAtLeastOne', { what: this.i18n.tr('cm:case.$').toLowerCase() });
        this.refreshTexts();
    }

    protected async canActivate(params: any, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<boolean> {
        this.checkPermissions();
        return true;
    }

    protected async activate(params: any, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        this.loadCaseStatuses();
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected selectedStatusChanged(): void {
        this.selectedStatusReason = null;
    }

    protected trackExpanded({ detail }: ExpandEvent<CaseOwnerWithLocalState>): void {
        if(detail.items) { expandedOwnersCache.set(this, detail.items); }
        if(detail.item) {
            const expanded = this.$table.isExpanded(detail.item);
            if(!detail.item._state) {
                detail.item._state = { isLoaded: expanded }
            } else {
                const isLoaded = safe(() => detail.item!._state!.isLoaded);
                if(!isLoaded) {
                    detail.item._state.isLoaded = this.$table.isExpanded(detail.item);
                }
            }
        }
    }

    // fired whenever the "choice" is changed between "assign" and "decline" or when a new user is selected
    protected assignmentChanged(event: Event) {
        // clear "isValid" flag for every case since a new user has been selected
        applyLocalState(this.verifiedCases, { isValid: null }, {});
        this.verifiedCases = [];

        // verify assignment of selected cases
        this.verifyAssignment();
    }

    protected casesLoaded({ detail }: CaseSelectCustomEvent): void {
        // new cases loaded, synchronize "isValid" state from previously verified cases
        for(const item of detail.cases) {
            const target = this.verifiedCases.find(c => c.id === item.id);
            // if there is a selected item, copy the isValid value, otherwise, default to null
            let isValid =
                !target ? null : (target._state && target._state.isValid);
            applyLocalState([item as CaseWithLocalState], { isValid }, {});
        }
    }

    protected casesSelected({ detail }: CaseSelectCustomEvent): void {
        let selectedCases = this.context.getSelectedCases(detail.ownerId);
        if(detail.casesRemoved.length === 1) {
            // a singular case has been removed
            const index = selectedCases.findIndex(cmCase => cmCase.id === detail.casesRemoved[0].id);
            if(index !== -1) selectedCases.splice(index, 1);
            this.context.setSelectedCases(detail.ownerId, selectedCases);
        } else if(detail.casesRemoved.length > 1) {
            // deselect all action has been taken, so remove all current page's cases from selection
            const retainedCases: CaseWithLocalState[] = [];
            selectedCases.forEach(cmCase => {
                const index = detail.casesRemoved?.findIndex(removedCase => removedCase.id === cmCase.id);
                if(index === -1) retainedCases.push(cmCase);
            });
            this.context.setSelectedCases(detail.ownerId, [...<Array<CM.Case>>detail.cases, ...retainedCases]);
        } else {
            // a case (or cases) have been selected, add previously-unselected cases to selection
            detail.cases.forEach(cmCase => {
                const index = selectedCases.findIndex(selectedCase => selectedCase.id === cmCase.id);
                if(index === -1) selectedCases.push(cmCase);
            });
            this.context.setSelectedCases(detail.ownerId, selectedCases);
        }
        this.selectedCount = this.context.getSelectedCases().length;

        // verify assignment of selected cases
        this.verifyAssignment();
        this.refreshTexts();
    }

    protected openCaseQueueHistory({ detail }: OpenCaseQueuesCustomEvent) {
        if(detail.case && detail.case.id) {
            this.caseQueueHistory = detail.case;
            this.$queueHistory.caseId = detail.case.id;
            this.$modal.open();
        }
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const sorting = this.sorting;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.loadOwnersWithCases();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseAssign = this.accountContext.hasPermission('CM.Case.Assign');
        if(!this.hasCaseAssign) {
            this.choice = 'decline';
            this.logger.warn('User does not have appropriate permissions to assign cases');
        }
    }

    private async reload(): Promise<void> {
        this.clearBulkAction();
        this.loadActiveCaseOwners();
        this.loadOwnersWithCases();
        // Close all detailed rows to force reload the owner case tables after assigning/declining cases
        if(this.$table) this.$table.closeAll();
    }

    private restoreExpanded() {
        // we need to give aurelia a chance to bind the goals into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            const expandedOwners = expandedOwnersCache.get(this);
            if(expandedOwners !== undefined) {
                expandedOwners.forEach((owner) => {
                    const foundOwner = this.ownersWithCases.find((o) => owner.id === o.id);
                    if(!!foundOwner) {
                        this.$table.open(foundOwner);
                    }
                });
            }
        });
    }

    private isVerifiedCase(item: CaseWithLocalState): boolean {
        const result = (item._state && item._state.isValid !== undefined && item._state.isValid !== null) || false;
        return result;
    }

    private calculateInvalidCount(): void {
        const selected = this.context.getSelectedCases();
        let count = 0;
        for(const selectedCase of selected) {
            if(selectedCase._state && selectedCase._state.isValid === false) {
                count++;
            }
        }
        this.invalidCount = count;
    }

    private async verifyAssignment(): Promise<void> {
        try {
            this.state.beginProcessing();

            // keep track of most recent verification attempt
            const previousState = ++this.verifyState;

            // get selected cases except for ones that have already been verified
            const selected = this.context.getSelectedCases().filter(c => !this.isVerifiedCase(c));
            const cases = getReferences(selected);

            // make sure there's actually work to do
            if(this.choice !== 'assign' || !this.selectedUser || !this.selectedUser.id || !cases || !cases.length) {
                this.invalidCount = 0;
                return;
            }

            const response = await this.service.verifyAssignment(this.selectedUser.id, cases);

            if(previousState !== this.verifyState) {
                this.logger.info(this.i18n.tr('ui:messages.warnings.verificationInterruptionDiscardResults'));
            }
            else {
                // only stash results if another newer request has not been made
                if(response.isSuccessful && response.result.filter && response.result.filter.items) {
                    // set all 'isValid' to false
                    applyLocalState(selected, { isValid: false }, {});
                    for(const item of response.result.filter.items) {
                        const found = selected.find(c => c.id === item.id);
                        if(found) {
                            // switch 'isValid' to true
                            applyLocalState([found], { isValid: true }, {});
                        }
                    }
                    // keep a list of all verified cases so we don't check them again
                    this.verifiedCases.push(...selected);
                }
            }
        }
        finally {
            this.state.endProcessing();
            this.calculateInvalidCount();
        }
    }

    protected refreshTexts() {
        this.selectedSingular = this.i18n.tr('ui:messages.info.selected', { what: this.i18n.tr('cm:case.$').toLowerCase(), number: this.selectedCount });
        this.selectedPlural = this.i18n.tr('ui:messages.info.selected', { what: this.i18n.tr('cm:case.$_plural').toLowerCase(), number: this.selectedCount });
    }

    protected clearBulkAction(): void {
        // Deselect the owner cases grids first before clearing.
        this.events.publish(new AssignCasesClearBulkActionEvent);

        this.context.clear();
        this.selectedUser = null;
        this.selectedCount = 0;
        this.selectedStatus = null;
        this.selectedStatusReason = null;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCaseStatuses(): Promise<void> {
        const includes: string[] = ['reasons'];
        const fields: string[] = ['description', 'reasons.description'];
        const flags: CM.CaseStatusFlags = {
            isDeclined: true
        }

        try {
            this.state.beginLoad();
            const response = await this.service.loadCaseStatuses({ all: true }, includes, flags, fields);
            this.statuses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    /** Loads active case owners for the "Assign to" drop down */
    @apiCall
    private async loadActiveCaseOwners(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };
        const includes: string[] = [];
        const fields: string[] = ['description'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCaseOwners(options, includes, false, null, fields);
            this.owners = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadOwnersWithCases(): Promise<void> {
        this.context.clear();

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            includeInactive: true
        };
        const includes: string[] = [];
        const fields: string[] = [
            'description',
            'openCaseCount',
            'totalAcuity'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCaseOwners(options, includes, true, 'includeUnassigned', fields);
            const results: CaseOwnerWithLocalState[] = response.results
                .map(r => {
                    (<CaseOwnerWithLocalState>r)._state = { isLoaded: false }
                    return r;
                });

            this.ownersWithCases = results;
            this.resultCount = response.count || 0;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async assignCases(): Promise<void> {
        if(!this.selectedUser || !this.selectedUser.id) {
            this.logger.error('Selected user is not set!');
            this.toaster.error().message('Selected User not set!').show();
            return;
        }

        // make sure verification has finished running
        await this.verifyAssignment();
        if(this.invalidCount > 0) {
            return;
        }

        const assignCases = this.context.getSelectedCases()
            .map(c => {
                return {
                    id: c.id,
                    assignedTo: getReference(this.selectedUser)
                } as CM.Case
            });

        try {
            this.state.beginSave();
            const progressToast = this.toaster.standard.batch('cm:case.$', assignCases.length);
            progressToast.show();

            const responses = await this.service.assignCasesBatch(assignCases, progressToast);
            const success = allActionsSucceeded(responses);
            this.toaster.standard.assigned('cm:case.$', success, assignCases.length);
            if(success) { this.reload(); }
        } catch(error) {
            this.toaster.standard.assigned('cm:case.$', false, assignCases.length);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async declineCases(): Promise<void> {
        if(!this.selectedStatus || (this.selectedStatus?.reasons?.length && !this.selectedStatusReason)) {
            return;
        }
        let success = false;

        const declineCases = this.context.getSelectedCases()
            .map(c => {
                return {
                    id: c.id,
                    status: getReference(this.selectedStatus, {
                        reason: getReference(this.selectedStatusReason)
                    })
                } as CM.Case
            });

        try {
            this.state.beginSave();
            const progressToast = this.toaster.standard.batch('cm:case.$', declineCases.length);
            progressToast.show();

            // Execute all promises and wait until they all finish
            const responses = await this.service.updateBatchCases(declineCases, progressToast);
            success = allActionsSucceeded(responses);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.updated('cm:case.$', success, declineCases.length);
            if(success) { this.reload(); }
            this.state.endSave();
        }
    }
}
